import { defineComponent as _defineComponent } from 'vue';
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "dependencies-section" };
const _hoisted_2 = { class: "d-flex align-center justify-space-between" };
const _hoisted_3 = { class: "d-flex align-center" };
const _hoisted_4 = { class: "mt-3" };
const _hoisted_5 = { class: "d-flex justify-end gap-2" };
const _hoisted_6 = {
    key: 0,
    class: "mt-2"
};
const _hoisted_7 = { class: "d-flex flex-wrap gap-1" };
const _hoisted_8 = {
    key: 0,
    class: "d-flex justify-center py-8"
};
const _hoisted_9 = {
    key: 1,
    class: "text-center py-8"
};
const _hoisted_10 = { class: "text-body-2 text-error" };
const _hoisted_11 = {
    key: 2,
    class: "text-center py-8 text-medium-emphasis"
};
const _hoisted_12 = { class: "tooltip-header" };
const _hoisted_13 = { class: "tooltip-row" };
const _hoisted_14 = { class: "tooltip-row" };
const _hoisted_15 = { class: "tooltip-row" };
const _hoisted_16 = {
    key: 0,
    class: "tooltip-row"
};
const _hoisted_17 = {
    key: 1,
    class: "tooltip-row"
};
const _hoisted_18 = {
    key: 4,
    class: "graph-legend mt-3"
};
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import * as d3 from "d3";
import { filesize as filesizeLib } from "filesize";
import { useStore } from "@/store";
import { formatDateTime } from "@/utils/dateUtils";
import { dserverApi } from "@/services/dserverApi";
// Emits
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatasetDependencies',
    emits: ["select-dataset"],
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const store = useStore();
        // Default dependency keys matching the server defaults
        const defaultDependencyKeys = [
            "readme.derived_from.uuid",
            "annotations.source_dataset_uuid",
        ];
        // Dependency keys configuration
        const dependencyKeys = ref([...defaultDependencyKeys]);
        const showSettings = ref(false);
        // Refs
        const graphContainer = ref(null);
        const svgElement = ref(null);
        const loading = ref(false);
        const error = ref(null);
        const graphData = ref([]);
        const hoveredNode = ref(null);
        const tooltipPosition = ref({ x: 0, y: 0 });
        // D3 simulation reference
        let simulation = null;
        // UUID of the dataset the graph was last rendered for (to decide whether to
        // preserve the user's zoom/pan state across re-renders)
        let lastRenderedUuid = null;
        const currentDataset = computed(() => store.current_dataset);
        // Reset dependency keys to defaults
        function resetDependencyKeys() {
            dependencyKeys.value = [...defaultDependencyKeys];
        }
        const hasConnections = computed(() => {
            if (graphData.value.length <= 1)
                return false;
            // Check if any dataset has derived_from connections
            return graphData.value.some((d) => d.derived_from && d.derived_from.length > 0);
        });
        // Watch for dataset changes
        watch(() => currentDataset.value?.uuid, async (newUuid) => {
            if (newUuid) {
                await loadGraph();
            }
        }, { immediate: true });
        // Watch for when the graph container becomes available (tab becomes visible)
        watch([graphContainer, svgElement], ([container, svg], [oldContainer]) => {
            if (resizeObserver && container !== oldContainer) {
                if (oldContainer)
                    resizeObserver.unobserve(oldContainer);
                if (container)
                    resizeObserver.observe(container);
            }
            if (container && svg && graphData.value.length > 0) {
                renderGraph();
            }
        });
        async function loadGraph() {
            const uuid = currentDataset.value?.uuid;
            if (!uuid)
                return;
            loading.value = true;
            error.value = null;
            try {
                // Pass custom dependency keys if they differ from defaults or if explicitly set
                const keysToUse = dependencyKeys.value.length > 0 ? dependencyKeys.value : undefined;
                const data = await dserverApi.getDependencyGraph(uuid, keysToUse);
                // Bail out if the current dataset changed while the request was in flight
                if (currentDataset.value?.uuid !== uuid)
                    return;
                graphData.value = data;
                await nextTick();
                if (currentDataset.value?.uuid !== uuid)
                    return;
                renderGraph();
            }
            catch (err) {
                if (currentDataset.value?.uuid !== uuid)
                    return;
                console.error("Failed to load dependency graph:", err);
                error.value = "Failed to load dependency graph";
                graphData.value = [];
            }
            finally {
                if (currentDataset.value?.uuid === uuid) {
                    loading.value = false;
                }
            }
        }
        function renderGraph() {
            if (!svgElement.value ||
                !graphContainer.value ||
                graphData.value.length === 0)
                return;
            // Preserve zoom/pan state when re-rendering the same graph (e.g. on resize);
            // reset to identity when rendering a new dataset
            const currentUuid = currentDataset.value?.uuid;
            const previousTransform = lastRenderedUuid === currentUuid
                ? d3.zoomTransform(svgElement.value)
                : d3.zoomIdentity;
            lastRenderedUuid = currentUuid ?? null;
            // Clear previous graph
            d3.select(svgElement.value).selectAll("*").remove();
            if (simulation)
                simulation.stop();
            const container = graphContainer.value;
            const width = container.clientWidth;
            const height = Math.max(400, container.clientHeight);
            const svg = d3
                .select(svgElement.value)
                .attr("width", width)
                .attr("height", height)
                .attr("viewBox", [0, 0, width, height]);
            // Create zoom behavior
            const zoom = d3
                .zoom()
                .scaleExtent([0.1, 4])
                .on("zoom", (event) => {
                g.attr("transform", event.transform);
            });
            svg.call(zoom);
            // Create container group for zoom/pan
            const g = svg.append("g");
            // Re-apply the previous zoom/pan state (identity for a new dataset)
            svg.call(zoom.transform, previousTransform);
            // Define arrow marker for directed edges
            const defs = svg.append("defs");
            defs
                .append("marker")
                .attr("id", "arrowhead")
                .attr("viewBox", "0 -5 10 10")
                .attr("refX", 20) // Position relative to target node (accounting for radius ~15)
                .attr("refY", 0)
                .attr("orient", "auto")
                .attr("markerWidth", 8)
                .attr("markerHeight", 8)
                .attr("xoverflow", "visible")
                .append("path")
                .attr("d", "M 0,-4 L 8,0 L 0,4")
                .attr("fill", "#666")
                .attr("stroke", "none");
            // Build nodes and links
            const nodes = graphData.value.map((d) => ({
                id: d.uuid,
                uuid: d.uuid,
                name: d.name,
                uri: d.uri,
                base_uri: d.base_uri,
                creator_username: d.creator_username,
                created_at: d.created_at,
                frozen_at: d.frozen_at,
                size_in_bytes: d.size_in_bytes,
                number_of_items: d.number_of_items,
                isCurrent: d.uuid === currentUuid,
            }));
            const nodeMap = new Map(nodes.map((n) => [n.uuid, n]));
            const links = [];
            // Build directed links from derived_from relationships
            // Arrow points from source (parent) to target (derived dataset)
            // Note: derived_from contains UUID strings, not objects
            graphData.value.forEach((dataset) => {
                if (dataset.derived_from) {
                    dataset.derived_from.forEach((depUuid) => {
                        // depUuid is a string (the parent dataset's UUID)
                        if (typeof depUuid === "string" && nodeMap.has(depUuid)) {
                            links.push({
                                source: depUuid,
                                target: dataset.uuid,
                            });
                        }
                    });
                }
            });
            // Create simulation
            simulation = d3
                .forceSimulation(nodes)
                .force("link", d3
                .forceLink(links)
                .id((d) => d.id)
                .distance(150))
                .force("charge", d3.forceManyBody().strength(-400))
                .force("center", d3.forceCenter(width / 2, height / 2))
                .force("collision", d3.forceCollide().radius(50));
            // Draw links
            const link = g
                .append("g")
                .attr("class", "links")
                .selectAll("line")
                .data(links)
                .join("line")
                .attr("stroke", "#666")
                .attr("stroke-opacity", 0.8)
                .attr("stroke-width", 2)
                .attr("marker-end", "url(#arrowhead)");
            // Draw nodes
            const node = g
                .append("g")
                .attr("class", "nodes")
                .selectAll("g")
                .data(nodes)
                .join("g")
                .attr("class", "node")
                .call(drag(simulation));
            // Node circles
            node
                .append("circle")
                .attr("r", (d) => (d.isCurrent ? 20 : 15))
                .attr("fill", (d) => (d.isCurrent ? "#1976D2" : "#42A5F5"))
                .attr("stroke", (d) => (d.isCurrent ? "#0D47A1" : "#1976D2"))
                .attr("stroke-width", (d) => (d.isCurrent ? 3 : 2))
                .style("cursor", "pointer");
            // Node labels
            node
                .append("text")
                .text((d) => truncateName(d.name, 15))
                .attr("dy", 35)
                .attr("text-anchor", "middle")
                .attr("font-size", "11px")
                .attr("fill", "#333");
            // Node interactions
            node
                .on("mouseenter", (event, d) => {
                hoveredNode.value = d;
                updateTooltipPosition(event);
            })
                .on("mousemove", (event) => {
                updateTooltipPosition(event);
            })
                .on("mouseleave", () => {
                hoveredNode.value = null;
            })
                .on("click", (event, d) => {
                if (!d.isCurrent) {
                    emit("select-dataset", d.uuid, d.uri);
                }
            });
            // Update positions on tick
            simulation.on("tick", () => {
                link
                    .attr("x1", (d) => d.source.x)
                    .attr("y1", (d) => d.source.y)
                    .attr("x2", (d) => d.target.x)
                    .attr("y2", (d) => d.target.y);
                node.attr("transform", (d) => `translate(${d.x},${d.y})`);
            });
        }
        function drag(sim) {
            function dragstarted(event) {
                if (!event.active)
                    sim.alphaTarget(0.3).restart();
                event.subject.fx = event.subject.x;
                event.subject.fy = event.subject.y;
            }
            function dragged(event) {
                event.subject.fx = event.x;
                event.subject.fy = event.y;
            }
            function dragended(event) {
                if (!event.active)
                    sim.alphaTarget(0);
                event.subject.fx = null;
                event.subject.fy = null;
            }
            return d3
                .drag()
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended);
        }
        function updateTooltipPosition(event) {
            const container = graphContainer.value;
            if (!container)
                return;
            const rect = container.getBoundingClientRect();
            tooltipPosition.value = {
                x: event.clientX - rect.left + 15,
                y: event.clientY - rect.top + 15,
            };
        }
        function truncateName(name, maxLength) {
            if (name.length <= maxLength)
                return name;
            return name.substring(0, maxLength - 3) + "...";
        }
        function formatDate(timestamp) {
            return formatDateTime(timestamp);
        }
        function filesize(bytes) {
            return filesizeLib(bytes);
        }
        // Handle resize
        let resizeObserver = null;
        onMounted(() => {
            resizeObserver = new ResizeObserver(() => {
                if (graphData.value.length > 0) {
                    renderGraph();
                }
            });
            if (graphContainer.value) {
                resizeObserver.observe(graphContainer.value);
            }
        });
        onUnmounted(() => {
            if (simulation)
                simulation.stop();
            if (resizeObserver)
                resizeObserver.disconnect();
        });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_combobox = _resolveComponent("v-combobox");
            const _component_v_expand_transition = _resolveComponent("v-expand-transition");
            const _component_v_chip = _resolveComponent("v-chip");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_component_v_card, {
                    variant: "outlined",
                    class: "mb-3 pa-3"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createVNode(_component_v_icon, {
                                    size: "18",
                                    class: "mr-2",
                                    color: "primary"
                                }, {
                                    default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                                            _createTextVNode("mdi-cog", -1)
                                        ]))]),
                                    _: 1
                                }),
                                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-caption text-medium-emphasis" }, "Dependency Keys", -1))
                            ]),
                            _createVNode(_component_v_btn, {
                                size: "x-small",
                                variant: "text",
                                icon: showSettings.value ? 'mdi-chevron-up' : 'mdi-chevron-down',
                                onClick: _cache[0] || (_cache[0] = ($event) => (showSettings.value = !showSettings.value))
                            }, null, 8, ["icon"])
                        ]),
                        _createVNode(_component_v_expand_transition, null, {
                            default: _withCtx(() => [
                                _withDirectives(_createElementVNode("div", _hoisted_4, [
                                    _createVNode(_component_v_combobox, {
                                        modelValue: dependencyKeys.value,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((dependencyKeys).value = $event)),
                                        items: defaultDependencyKeys,
                                        label: "Dependency keys (paths to UUID references)",
                                        variant: "outlined",
                                        density: "compact",
                                        multiple: "",
                                        chips: "",
                                        "closable-chips": "",
                                        hint: "e.g., readme.derived_from.uuid, annotations.source_dataset_uuid",
                                        "persistent-hint": "",
                                        class: "mb-2"
                                    }, null, 8, ["modelValue"]),
                                    _createElementVNode("div", _hoisted_5, [
                                        _createVNode(_component_v_btn, {
                                            size: "small",
                                            variant: "text",
                                            onClick: resetDependencyKeys
                                        }, {
                                            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                                    _createTextVNode(" Reset to defaults ", -1)
                                                ]))]),
                                            _: 1
                                        }),
                                        _createVNode(_component_v_btn, {
                                            size: "small",
                                            variant: "tonal",
                                            color: "primary",
                                            loading: loading.value,
                                            onClick: loadGraph
                                        }, {
                                            default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                                    _createTextVNode(" Apply ", -1)
                                                ]))]),
                                            _: 1
                                        }, 8, ["loading"])
                                    ])
                                ], 512), [
                                    [_vShow, showSettings.value]
                                ])
                            ]),
                            _: 1
                        }),
                        (!showSettings.value && dependencyKeys.value.length > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createElementVNode("div", _hoisted_7, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dependencyKeys.value, (key) => {
                                        return (_openBlock(), _createBlock(_component_v_chip, {
                                            key: key,
                                            size: "x-small",
                                            variant: "outlined",
                                            color: "primary"
                                        }, {
                                            default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(key), 1)
                                            ]),
                                            _: 2
                                        }, 1024));
                                    }), 128))
                                ])
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(_component_v_progress_circular, {
                            indeterminate: "",
                            color: "primary",
                            size: "48"
                        })
                    ]))
                    : (error.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createVNode(_component_v_icon, {
                                size: "48",
                                color: "error",
                                class: "mb-2"
                            }, {
                                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                        _createTextVNode("mdi-alert-circle", -1)
                                    ]))]),
                                _: 1
                            }),
                            _createElementVNode("p", _hoisted_10, _toDisplayString(error.value), 1),
                            _createVNode(_component_v_btn, {
                                size: "small",
                                variant: "tonal",
                                color: "primary",
                                class: "mt-2",
                                onClick: loadGraph
                            }, {
                                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                        _createTextVNode(" Retry ", -1)
                                    ]))]),
                                _: 1
                            })
                        ]))
                        : (!hasConnections.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createVNode(_component_v_icon, {
                                    size: "48",
                                    color: "grey-lighten-1",
                                    class: "mb-2"
                                }, {
                                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                            _createTextVNode("mdi-graph-outline", -1)
                                        ]))]),
                                    _: 1
                                }),
                                _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-body-2" }, "No dependency connections found", -1)),
                                _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-caption" }, " This dataset has no references matching the configured dependency keys ", -1))
                            ]))
                            : (_openBlock(), _createElementBlock("div", {
                                key: 3,
                                class: "graph-container",
                                ref_key: "graphContainer",
                                ref: graphContainer
                            }, [
                                (_openBlock(), _createElementBlock("svg", {
                                    ref_key: "svgElement",
                                    ref: svgElement,
                                    class: "graph-svg"
                                }, null, 512)),
                                (hoveredNode.value)
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 0,
                                        class: "graph-tooltip",
                                        style: _normalizeStyle({
                                            left: tooltipPosition.value.x + 'px',
                                            top: tooltipPosition.value.y + 'px',
                                        })
                                    }, [
                                        _createElementVNode("div", _hoisted_12, _toDisplayString(hoveredNode.value.name), 1),
                                        _createElementVNode("div", _hoisted_13, [
                                            _cache[11] || (_cache[11] = _createElementVNode("span", { class: "tooltip-label" }, "Creator:", -1)),
                                            _createElementVNode("span", null, _toDisplayString(hoveredNode.value.creator_username), 1)
                                        ]),
                                        _createElementVNode("div", _hoisted_14, [
                                            _cache[12] || (_cache[12] = _createElementVNode("span", { class: "tooltip-label" }, "Created:", -1)),
                                            _createElementVNode("span", null, _toDisplayString(formatDate(hoveredNode.value.created_at)), 1)
                                        ]),
                                        _createElementVNode("div", _hoisted_15, [
                                            _cache[13] || (_cache[13] = _createElementVNode("span", { class: "tooltip-label" }, "Frozen:", -1)),
                                            _createElementVNode("span", null, _toDisplayString(formatDate(hoveredNode.value.frozen_at)), 1)
                                        ]),
                                        (hoveredNode.value.size_in_bytes)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                                _cache[14] || (_cache[14] = _createElementVNode("span", { class: "tooltip-label" }, "Size:", -1)),
                                                _createElementVNode("span", null, _toDisplayString(filesize(hoveredNode.value.size_in_bytes)), 1)
                                            ]))
                                            : _createCommentVNode("", true),
                                        (hoveredNode.value.number_of_items)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                                _cache[15] || (_cache[15] = _createElementVNode("span", { class: "tooltip-label" }, "Items:", -1)),
                                                _createElementVNode("span", null, _toDisplayString(hoveredNode.value.number_of_items), 1)
                                            ]))
                                            : _createCommentVNode("", true)
                                    ], 4))
                                    : _createCommentVNode("", true)
                            ], 512)),
                (hasConnections.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_18, [...(_cache[16] || (_cache[16] = [
                            _createStaticVNode("<div class=\"legend-item\" data-v-c40e1678><span class=\"legend-node current\" data-v-c40e1678></span><span class=\"text-caption\" data-v-c40e1678>Current dataset</span></div><div class=\"legend-item\" data-v-c40e1678><span class=\"legend-node related\" data-v-c40e1678></span><span class=\"text-caption\" data-v-c40e1678>Related dataset</span></div><div class=\"legend-item\" data-v-c40e1678><span class=\"legend-arrow\" data-v-c40e1678>→</span><span class=\"text-caption\" data-v-c40e1678>Derived from (dependency direction)</span></div>", 3)
                        ]))]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
